import { observable, action, runInAction } from 'mobx';
import AgoraRTC, {
    IAgoraRTCClient,
    ILocalAudioTrack,
    ILocalVideoTrack,
    IBufferSourceAudioTrack,
    AudioSourceState
} from 'agora-rtc-sdk-ng';

import { StoreExt, Loading } from '@utils/reactExt';
import {
    AGORA_RTC_LOG_LEVEL,
    AGORA_RTC_ERROR,
    TTS_PLAY_STATUS,
    VERIFICATION_TYPE,
    BAIDU_AI_INTERFACE_ERROR
} from '@constants/index';
import { to16kHz, to16BitPCM, queryURL, isIOS, replaceLetters } from '@utils/index';
import { recordStore, taskStore } from '..';

export class RtcStore extends StoreExt {
    client: IAgoraRTCClient | null = null;
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor() {
        super();
        AgoraRTC.setLogLevel(process.env.APP_ENV === 'prod' ? AGORA_RTC_LOG_LEVEL.ERROR : AGORA_RTC_LOG_LEVEL.DEBUG);
        this.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    }

    @observable
    localAudioTrack: ILocalAudioTrack | null = null;

    @observable
    ttsAudioTrackMap: Map<number, IBufferSourceAudioTrack> = new Map();

    @observable
    localVideoTrack: ILocalVideoTrack | null = null;

    @action
    async init(data: ITaskStore.IStartResponse): Promise<void> {
        try {
            this.$toast.show({
                icon: 'loading',
                content: '正在等待用户授权',
                duration: 0
            });
            const cameras = await AgoraRTC.getCameras(true);
            if (!cameras || cameras.length <= 0) {
                return Promise.reject({ code: AGORA_RTC_ERROR.VIDEO_NOT_SUPPORTED });
            }

            const microphones = await AgoraRTC.getMicrophones(true);
            if (!microphones || microphones.length <= 0) {
                return Promise.reject({ code: AGORA_RTC_ERROR.AUDIO_NOT_SUPPORTED });
            }

            // await AgoraRTC.getSupportedCodec();

            const [localAudioTrack, localVideoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
                { AEC: true, ANS: true, AGC: true },
                {
                    facingMode: 'user'
                }
            );

            this.localAudioTrack = localAudioTrack;
            this.localVideoTrack = localVideoTrack;
            this.localAudioTrack?.setVolume(isIOS() ? 100 : 70);

            // await this.client?.join(
            //     process.env.AGORA_APP_ID,
            //     data.activity.dualRecord.rtcRoomId,
            //     decodeURIComponent(data.activity.dualRecord.rtcRoomToken),
            //     data.activity.dualRecord.rtcRoomUId
            // );
            if (!data.config.hasSignature) {
                await this.client?.join(
                    process.env.AGORA_APP_ID,
                    data.activity.dualRecord.rtcRoomId,
                    decodeURIComponent(data.activity.dualRecord.rtcRoomToken),
                    data.activity.dualRecord.rtcRoomUId
                );
                await this.client?.publish([this.localAudioTrack!, this.localVideoTrack!]);
                recordStore.startRecordStream();
            }

            // this.localAudioTrack.setVolume(1000);
            this.$toast.clear();
        } catch (error: any) {
            console.log('=======init=======', error);
            const { code } = error;
            this.$toast.clear();
            const err: ITaskStore.IError = {
                error: {
                    errCode: code,
                    msg: ''
                },
                stack: ''
            };
            return Promise.reject(err);
        }
    }

    @Loading
    @action
    async play(data: ITaskStore.IStartResponse): Promise<boolean> {
        try {
            if (this.localAudioTrack && this.localVideoTrack) {
                await this.client?.join(
                    process.env.AGORA_APP_ID,
                    data.activity.dualRecord.rtcRoomId,
                    decodeURIComponent(data.activity.dualRecord.rtcRoomToken),
                    data.activity.dualRecord.rtcRoomUId
                );
                await this.client?.publish([this.localAudioTrack!, this.localVideoTrack!]);
                recordStore.startRecordStream();

                const tasks = data.steps;
                const ttsTasks = tasks.filter(
                    t =>
                        (t.type === VERIFICATION_TYPE.TTS_ASR ||
                            t.type === VERIFICATION_TYPE.TTS_ASR_STD ||
                            t.type === VERIFICATION_TYPE.TTS_ASR_SPEAKER) &&
                        t.content?.audio
                );

                const firstTtsTask = ttsTasks[0];
                if (firstTtsTask) {
                    await this.createTTSAudio(
                        firstTtsTask.orderId!,
                        firstTtsTask.content?.audio!,
                        data.activity.ttsToken!,
                        data._id
                    );
                    await this.mockPlayBufferAudio();
                }
                return true;
            }
            return false;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @Loading
    @action
    async publishAudioAndVideo(ele: string, data: ITaskStore.IStartResponse): Promise<void> {
        try {
            this.localVideoTrack?.play(ele, { mirror: true });
        } catch (error: any) {
            return Promise.reject(error);
        }
    }

    @action
    async stopPlayVideo(): Promise<void> {
        try {
            this.localVideoTrack?.stop();
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @action
    async unplulishAudioAndVideo(): Promise<void> {
        try {
            this.localAudioTrack?.close();
            this.localVideoTrack?.close();
            if (this.localAudioTrack && this.localVideoTrack) {
                this.client?.unpublish([this.localAudioTrack!, this.localVideoTrack!]);
            }
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @action
    async stop(): Promise<void> {
        try {
            await this.unplulishAudioAndVideo();
            await this.client?.leave();
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @Loading
    @action
    async mockPlayBufferAudio(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                const keys = Array.from(this.ttsAudioTrackMap.keys());
                for (const key of keys) {
                    const buffAudio = this.ttsAudioTrackMap.get(key);
                    buffAudio?.setMuted(true);
                    buffAudio?.startProcessAudioBuffer();
                    buffAudio?.play();
                    buffAudio?.stop();
                    buffAudio?.stopProcessAudioBuffer();
                }
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }

    @action
    getAudioDuration(key: number): number {
        const buffaudio = this.ttsAudioTrackMap.get(key);
        return buffaudio?.duration || 0;
    }

    @action
    async playBufferAudio(key: number, cb?: () => void): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                const buffAudio = this.ttsAudioTrackMap.get(key);

                if (
                    buffAudio?.currentState === TTS_PLAY_STATUS.STOPED ||
                    buffAudio?.currentState === TTS_PLAY_STATUS.PAUSED
                ) {
                    const statusChange = (currentState: AudioSourceState) => {
                        if (currentState === TTS_PLAY_STATUS.PLAYING) {
                            cb?.();
                        }
                        if (currentState === TTS_PLAY_STATUS.STOPED) {
                            buffAudio?.stop();
                            buffAudio?.stopProcessAudioBuffer();
                            if (
                                '666a5dc82efa150de5183115' === taskStore.task?.tenant ||
                                '668e7f852efa150de5183116' === taskStore.task?.tenant
                            ) {
                                this.localAudioTrack?.setVolume(isIOS() ? 100 : 70);
                            }
                            resolve();
                        }
                        if (currentState === TTS_PLAY_STATUS.STOPED || currentState === TTS_PLAY_STATUS.PAUSED) {
                            buffAudio?.stop();
                            buffAudio?.stopProcessAudioBuffer();
                            if (
                                '666a5dc82efa150de5183115' === taskStore.task?.tenant ||
                                '668e7f852efa150de5183116' === taskStore.task?.tenant
                            ) {
                                this.localAudioTrack?.setVolume(isIOS() ? 100 : 70);
                            }
                            buffAudio?.off('source-state-change', statusChange);
                        }
                    };
                    buffAudio?.on('source-state-change', statusChange);
                    await buffAudio?.setMuted(false);
                    if (
                        '666a5dc82efa150de5183115' === taskStore.task?.tenant ||
                        '668e7f852efa150de5183116' === taskStore.task?.tenant
                    ) {
                        this.localAudioTrack?.setVolume(0);
                    }
                    buffAudio?.startProcessAudioBuffer();
                    buffAudio?.play();
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    @action
    async bufferAudioIsPlaying(key: number): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            const buffAudio = this.ttsAudioTrackMap.get(key);
            const timer = setInterval(() => {
                if (buffAudio?.getVolumeLevel() !== 0) {
                    clearInterval(timer);
                    resolve(true);
                }
            }, 200);
        });
    }

    @action
    async stopBufferAudio(key: number): Promise<void> {
        return new Promise(async resolve => {
            try {
                const buffAudio = this.ttsAudioTrackMap.get(key);
                // this.client?.unpublish(buffAudio);
                await buffAudio?.setMuted(true);
                buffAudio?.stopProcessAudioBuffer();
                buffAudio?.stop();
                resolve();
            } catch (error) {
                resolve();
            }
        });
    }

    @action
    getAudioVolume(): number {
        return this.localAudioTrack?.getVolumeLevel() || 0;
    }

    @action
    getAudioData(node: ITaskStore.ISteps): () => Promise<ITaskStore.ISpeechRecognizeResponse> {
        const audioBuffer: any[] = [];
        // const oriBuffer: any[] = [];
        let sampleRate: number = 48000;
        this.localAudioTrack?.setAudioFrameCallback((buffer: AudioBuffer) => {
            sampleRate = buffer.sampleRate;
            // @ts-ignore
            audioBuffer.push(...new Float32Array(buffer.getChannelData(0).buffer));
        }, 16384);
        return async (): Promise<ITaskStore.ISpeechRecognizeResponse> => {
            try {
                this.localAudioTrack?.setAudioFrameCallback(null);

                const output = to16kHz(new Float32Array(audioBuffer), sampleRate);
                const audioData = to16BitPCM(output);
                const res = await this.$api.task.speechRecognize(new Int8Array(audioData.buffer).buffer, node.orderId);
                return res;
            } catch (error) {
                return Promise.reject(error);
            }
        };
    }

    @action
    async createTTSAudio(key: number, text: string, ttsToken: string, cuid: string): Promise<void> {
        try {
            if (!this.ttsAudioTrackMap.has(key) || !this.ttsAudioTrackMap.get(key)) {
                let textStr = text;
                if (
                    '666a5dc82efa150de5183115' === taskStore.task?.tenant ||
                    '668e7f852efa150de5183116' === taskStore.task?.tenant
                ) {
                    textStr = replaceLetters(text);
                }

                const bufferAudioTrack = await AgoraRTC.createBufferSourceAudioTrack({
                    source: `${process.env.BASE_URL}/verify/orderId/${key}/skill/text2audio/?text=${encodeURIComponent(
                        textStr
                    )}&authorization=${queryURL('token')}`
                });
                await this.client?.publish(bufferAudioTrack);
                runInAction(() => {
                    if (this.ttsAudioTrackMap.has(key)) {
                        this.ttsAudioTrackMap.delete(key);
                    }
                    this.ttsAudioTrackMap.set(key, bufferAudioTrack);
                });
            }
        } catch (error) {
            return Promise.reject({
                error: { errCode: BAIDU_AI_INTERFACE_ERROR.BAIDU_AI_TTS_ERROR, msg: '文字转语音接口报错' }
            });
        }
    }

    @Loading
    @action
    async process(data: ITaskStore.IStartResponse): Promise<void> {
        try {
            // const promises: Promise<any>[] = [];

            const res = await this.$api.task.verify();

            if (res.taskState !== 10 && res.taskState !== 11) {
                return Promise.reject('核验已关闭');
            }

            await this.init(data);

            // tasks.forEach(task => {
            //     if (
            //         (task.type === VERIFICATION_TYPE.TTS_ASR ||
            //             task.type === VERIFICATION_TYPE.TTS_ASR_STD ||
            //             task.type === VERIFICATION_TYPE.TTS_ASR_SPEAKER) &&
            //         task.content?.audio
            //     ) {
            //         promises.push(
            //             this.createTTSAudio(task.orderId!, task.content.audio, data.activity.ttsToken!, data._id)
            //         );
            //     }
            // });
            // await Promise.all(promises);
            if (!data.config.hasSignature) {
                this.$toast.show({
                    icon: 'loading',
                    content: 'Loadig...',
                    duration: 0
                });
                const tasks = data.steps;
                const ttsTasks = tasks.filter(
                    t =>
                        (t.type === VERIFICATION_TYPE.TTS_ASR ||
                            t.type === VERIFICATION_TYPE.TTS_ASR_STD ||
                            t.type === VERIFICATION_TYPE.TTS_ASR_SPEAKER) &&
                        t.content?.audio
                );

                const firstTtsTask = ttsTasks[0];

                if (firstTtsTask) {
                    await this.createTTSAudio(
                        firstTtsTask.orderId!,
                        firstTtsTask.content?.audio!,
                        data.activity.ttsToken!,
                        data._id
                    );
                    await this.mockPlayBufferAudio();
                }
            }
            this.$toast.clear();
        } catch (err) {
            const { error } = err as ITaskStore.IError;
            if (
                !(
                    error?.errCode === AGORA_RTC_ERROR.NOT_READABLE ||
                    error?.errCode === AGORA_RTC_ERROR.AUDIO_NOT_SUPPORTED ||
                    error?.errCode === AGORA_RTC_ERROR.VIDEO_NOT_SUPPORTED ||
                    error?.errCode === AGORA_RTC_ERROR.NOT_SUPPORTED
                )
            ) {
                this.$toast.clear();
            }
            return Promise.reject(err);
        } finally {
            this.$toast.clear();
        }
    }

    @Loading
    @action
    async destroy(): Promise<void> {
        try {
            if (this.localAudioTrack) {
                this.localAudioTrack.stop();
            }
            if (this.localVideoTrack) {
                this.localVideoTrack.stop();
            }

            if (this.client) {
                this.client.leave();
            }
            return;
        } catch (error) {
            return Promise.resolve();
        }
    }
}

export default new RtcStore();
